import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Alert } from '@mui/material';
import { useLocale } from '../../i18n/LanguageContext';

function FlowBlock({ title, steps, testId, statusLabel }) {
  return (
    <Box data-testid={testId} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {steps.map((step, index) => (
        <Box key={`${title}-${step.status}-${index}`}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 1.5,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {index + 1}. {step.actor} → {step.action}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {statusLabel}: {step.status}
            </Typography>
            {step.note && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {step.note}
              </Typography>
            )}
          </Box>
          {index < steps.length - 1 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 0.5 }}>
              ↓
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export default function OrderStatusFlowDiagram() {
  const { t } = useLocale();
  const [activeTab, setActiveTab] = useState('pickup');

  const upfrontSteps = [
    {
      actor: t('home.orderFlow.roles.customer'),
      action: t('home.orderFlow.upfront.step1'),
      status: t('home.orderFlow.statuses.placed'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.upfront.step2'),
      status: t('home.orderFlow.statuses.accepted'),
      note: t('home.orderFlow.upfront.note2'),
    },
    {
      actor: t('home.orderFlow.roles.customer'),
      action: t('home.orderFlow.upfront.step3'),
      status: t('home.orderFlow.statuses.paid'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.upfront.step4'),
      status: t('home.orderFlow.statuses.inProgress'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.upfront.step5'),
      status: t('home.orderFlow.statuses.readyOrOut'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.upfront.step6'),
      status: t('home.orderFlow.statuses.completed'),
      note: null,
    },
  ];

  const payAtPickupSteps = [
    {
      actor: t('home.orderFlow.roles.customer'),
      action: t('home.orderFlow.pickup.step1'),
      status: t('home.orderFlow.statuses.placed'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.pickup.step2'),
      status: t('home.orderFlow.statuses.accepted'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.pickup.step3'),
      status: t('home.orderFlow.statuses.inProgress'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.pickup.step4'),
      status: t('home.orderFlow.statuses.readyOrOut'),
      note: null,
    },
    {
      actor: t('home.orderFlow.roles.chef'),
      action: t('home.orderFlow.pickup.step5'),
      status: t('home.orderFlow.statuses.completed'),
      note: t('home.orderFlow.pickup.note5'),
    },
  ];

  const activeTitle = activeTab === 'pickup'
    ? t('home.orderFlow.titles.pickup')
    : t('home.orderFlow.titles.upfront');

  const activeSteps = activeTab === 'pickup' ? payAtPickupSteps : upfrontSteps;

  return (
    <Box data-testid="order-status-flow-diagram" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Alert severity="info" sx={{ mb: 0 }}>
        {t('home.orderFlow.reminder')}
      </Alert>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          aria-label="payment flow tabs"
        >
          <Tab
            value="pickup"
            label={t('home.orderFlow.tabs.pickup')}
            data-testid="order-flow-tab-pickup"
          />
          <Tab
            value="upfront"
            label={t('home.orderFlow.tabs.upfront')}
            data-testid="order-flow-tab-upfront"
          />
        </Tabs>
      </Box>

      <FlowBlock
        title={activeTitle}
        steps={activeSteps}
        testId={activeTab === 'pickup' ? 'order-status-flow-pickup' : 'order-status-flow-upfront'}
        statusLabel={t('home.orderFlow.statusLabel')}
      />
    </Box>
  );
}
